import { useEffect, useState, type MutableRefObject } from 'react';
import type { Appearance } from '@stripe/stripe-js';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PhoneNumberInput, { isValidPhoneNumber } from 'react-phone-number-input/input';
import type { Country } from 'react-phone-number-input';
import type { BillingOutcome } from '@/lib/billing-card';
import type { PaymentMethod } from './queries';
import { COUNTRY_ITEMS, defaultCountry } from '@/components/alert-phone-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** AIT-505 — the authenticated surface (org billing) and the public add-card
 *  link (Task 8) share this exact card + phone + OTP form; each wrapper mints
 *  its own SetupIntent and owns its own `<Elements>`, and hands this component
 *  the thin set of server calls it needs through `CardCaptureApi`. */
export interface CardCaptureApi {
  persistCard(setupIntent: { id: string; payment_method?: string | { id: string } | null }): Promise<
    { ok: true; billingOutcome: BillingOutcome } | { ok: false; message: string }
  >;
  sendOtp(input: { phone: string }): Promise<{ sent: boolean; message?: string }>;
  verifyOtp(code: string): Promise<{ ok: boolean; message?: string }>;
  /** Saved-card chip brand/last4 — optional; a wrapper with nowhere to list
   *  cards (e.g. no authenticated session yet) can omit it and the chip just
   *  shows the checkmark without brand/last4. */
  listCards?(): Promise<PaymentMethod[]>;
}

/** AIT-494 — ONE modal: card + phone together, then the OTP code inline.
 *  Stripe cannot require a bare phone (PaymentElement FieldOption is only
 *  auto/never; requiring one drags in the full AddressElement, rejected for
 *  bulk), so the phone row is ours — the same input the launchpad alert-phone
 *  dialog uses. Verifying is the consent signature for alerts (AIT-383). */
/** Mirror the PaymentElement's .Input appearance (buildAppearance) so the
 *  phone row reads as one form with the Stripe fields above it. */
const STRIPE_FIELD =
  'h-[34px] rounded-[8px] border-[#ebebeb] bg-transparent px-2.5 py-0 text-sm shadow-none dark:border-white/15 dark:bg-white/5';

export function CardCapture({ api, needsPhone, phoneStatusPending, savingRef, onSaved, onVerified, onClose, onOutcome }: {
  api: CardCaptureApi;
  needsPhone: boolean;
  /** The alert-phone status lookup is still in flight — hold Save so a fast
   *  customer can't slip past the phone step before we know it's needed
   *  (Codex PR #260). */
  phoneStatusPending: boolean;
  /** Mirrors `submitting` up to the Dialog wrapper so a dismissal can't land
   *  mid-save (Codex PR #260): the card may persist AFTER the parent already
   *  cleared the pending pick and told the customer nothing changed. */
  savingRef: MutableRefObject<boolean>;
  onSaved: () => void;
  onVerified: () => void;
  onClose: () => void;
  /** AIT-505 — what saving the card actually did server-side, beyond storing
   *  it (resumed a paused plan, or the resume charge failed). The wrapper
   *  also uses a 'charge_failed' outcome here to drive its own charge-failed
   *  banner + remint — there is no separate callback for that. */
  onOutcome?: (outcome: BillingOutcome) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  // form: card + phone. verify: card already SAVED, OTP send failed or needs a
  // retry — the card section is replaced by a saved-card chip and only the
  // phone controls remain. code: enter the 6-digit code. The dialog never
  // swaps to another modal; the same body morphs through the stages, and once
  // the card is saved nothing later (failed send, wrong code, skip) touches it.
  const [stage, setStage] = useState<'form' | 'verify' | 'code'>('form');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<Country>(defaultCountry);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which accordion method is picked (card / google_pay / …). Until the
  // customer commits to one, the phone row stays out of the way.
  const [method, setMethod] = useState<string | null>(null);
  // One line instead of hand-marking every submit() exit — the ref tracks
  // whatever `submitting` is, and the wrapper reads it synchronously.
  useEffect(() => {
    savingRef.current = submitting;
    // Codex round 7 (P2) — belt-and-braces alongside the wrapper's explicit
    // reset before a remint: if THIS instance unmounts while `submitting` was
    // still true (a remint re-keys <Elements>, or a follow-up mint fails and
    // no CardCapture remounts at all), nothing else is left to ever flip the
    // ref back to false, and a savingRef-gated dialog stays unclosable.
    return () => { savingRef.current = false; };
  }, [submitting, savingRef]);

  /** POST the phone + send the OTP. True = code sent, flip to the code stage. */
  async function sendOtp(): Promise<boolean> {
    const result = await api.sendOtp({ phone });
    if (!result.sent) {
      // The card's fate is the chip's job — this line is only about the code.
      setError(result.message ?? 'We could not send the code to that number. Try again.');
      return false;
    }
    return true;
  }

  async function submit() {
    if (!stripe || !elements) return;
    // The phone row only renders once a method is picked — don't demand a
    // field the customer cannot see (Stripe raises its own "select a payment
    // method" error below).
    const phoneWanted = needsPhone && method != null;
    if (phoneWanted && (!phone || !isValidPhoneNumber(phone))) {
      const countryName = COUNTRY_ITEMS.find((c) => c.value === country)?.name ?? country;
      setError(`That is not a valid ${countryName} number. Check the country next to it.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    const { error, setupIntent } = await stripe.confirmSetup({ elements, redirect: 'if_required' });
    if (error) { setSubmitting(false); setError(error.message ?? 'Could not save card'); return; }
    // Persist the card SYNCHRONOUSLY so the immediate subscription refetch sees
    // hasCard=true (the setup_intent.succeeded webhook can lag; it stays a backstop).
    // CRITICAL: only close on SUCCESS. If the POST fails (4xx/5xx), throws, or the
    // intent has no id, surface the error and keep the dialog open — closing here
    // would re-create the exact "card did not stick" bounce this fix removes.
    const result = await api.persistCard(setupIntent);
    if (!result.ok) { setSubmitting(false); setError(result.message); return; }
    onOutcome?.(result.billingOutcome);
    // AIT-505 — the card saved fine but the resume charge it was meant to
    // cover was declined. The SAME dialog stays open on a FRESH SetupIntent
    // (the just-used one is spent) so the customer can retry with another
    // card, instead of a silent success that leaves the plan still paused.
    // The wrapper's onOutcome handler owns the fresh-mint + banner for this
    // outcome; this component only stops short of onSaved/the phone flow.
    if (result.billingOutcome === 'charge_failed') {
      setSubmitting(false);
      return;
    }
    onSaved();
    if (!phoneWanted) { setSubmitting(false); onClose(); return; }
    // Card is saved either way from here on — the phone flow only decides
    // which stage shows next. The card section never comes back: a failed
    // send lands on the verify stage (saved-card chip + retry), never back on
    // the form, and nothing about the card is at risk anymore.
    try {
      const sent = await sendOtp();
      setSubmitting(false);
      setStage(sent ? 'code' : 'verify');
      if (sent) setError(null);
    } catch {
      setSubmitting(false);
      setStage('verify');
      setError('We could not send the code to that number. Try again.');
    }
  }

  /** verify-stage retry — the card is long saved; this only re-sends the code. */
  async function resendOtp() {
    if (!phone || !isValidPhoneNumber(phone)) {
      const countryName = COUNTRY_ITEMS.find((c) => c.value === country)?.name ?? country;
      setError(`That is not a valid ${countryName} number. Check the country next to it.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const sent = await sendOtp();
      if (sent) { setStage('code'); setError(null); }
    } catch {
      setError('We could not send a code to that number. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCode() {
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.verifyOtp(code);
      if (!result.ok) { setError(result.message ?? 'That code is not correct.'); return; }
      onVerified();
      toast.success('Phone verified. Alerts are on.');
      onClose();
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Shared between the form and verify stages — same controls, the only
  // difference is whether the card section still exists above them.
  const phoneBlock = (
    <div className="space-y-2">
      <Label htmlFor="card-alert-phone">Phone number</Label>
      <div className="flex items-center gap-2">
        <Select
          items={COUNTRY_ITEMS}
          value={country as string}
          onValueChange={(v) => {
            // Clearing the number on country change stops a leftover
            // national number from silently re-keying to a new calling
            // code (+1 555… becoming +44 555…).
            setCountry((v ?? 'US') as Country);
            setPhone('');
          }}
        >
          <SelectTrigger aria-label="Country" className={`w-auto shrink-0 gap-1 data-[size=default]:h-[34px] ${STRIPE_FIELD}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent alignItemWithTrigger={false} align="start" className="max-h-72 min-w-64">
            {COUNTRY_ITEMS.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.name} +{c.callingCode}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <PhoneNumberInput
          id="card-alert-phone"
          country={country}
          value={phone}
          onChange={(v) => setPhone(v ?? '')}
          inputComponent={Input}
          autoComplete="tel"
          className={STRIPE_FIELD}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        We will text you here if anything ever breaks, plus product news and
        occasional marketing. Opt out anytime.
      </p>
    </div>
  );

  if (stage === 'verify') {
    return (
    <div className="flex flex-col gap-4 antialiased">
        <CardSavedChip api={api} />
        {phoneBlock}
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Skip for now</Button>
          <Button onClick={resendOtp} disabled={submitting || !phone || !isValidPhoneNumber(phone)}>
            {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {submitting ? 'Sending…' : 'Send code'}
          </Button>
        </div>
      </div>
    );
  }

  if (stage === 'code') {
    return (
    <div className="flex flex-col gap-4 antialiased">
        <CardSavedChip api={api} />
        <p className="text-sm text-muted-foreground">
          We sent a 6-digit code to {phone}.
        </p>
        <div className="space-y-2">
          <Label htmlFor="card-phone-code">Verification code</Label>
          <Input
            id="card-phone-code"
            placeholder="123456"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end">
          <Button onClick={submitCode} disabled={submitting || !/^\d{6}$/.test(code)}>
            {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {submitting ? 'Verifying…' : 'Verify'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    // antialiased: Stripe's iframe smooths its text; without matching it the
    // same Geist at the same weight looks bolder on our rows than theirs.
    <div className="flex flex-col gap-4 antialiased">
      {/* Wallets wanted (product call 2026-08-27): PaymentElement accordion
          with Card / Apple Pay / Google Pay. The method header is Stripe's and
          non-removable — with several methods it is a real selector, so it
          earns its row. Link stays off; terms line stays off. */}
      <PaymentElement
        options={{
          layout: { type: 'accordion', radios: 'never', spacedAccordionItems: true },
          wallets: { link: 'never', applePay: 'auto', googlePay: 'auto' },
          terms: { card: 'never' },
        }}
        onChange={(e) => setMethod(e.value?.type ?? null)}
      />
      {needsPhone && method != null && phoneBlock}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end">
        <Button onClick={submit} disabled={submitting || phoneStatusPending}>
          {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

/** "Your card is on file" — appears after the SetupIntent is persisted, in
 *  place of the card fields. Brand + last4 come from `api.listCards`, fetched
 *  once on mount (the card is already saved server-side by the time this
 *  chip appears); while it loads, the chip still confirms the save. */
function CardSavedChip({ api }: { api: CardCaptureApi }) {
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api.listCards?.().then((data) => {
      if (!cancelled) setMethods(data);
    });
    return () => { cancelled = true; };
  }, [api]);
  const pm = methods.find((m) => m.isDefault) ?? methods[0];
  const brand = pm?.brand ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1) : null;
  return (
    <div className={`flex items-center gap-2 px-2.5 py-2 text-sm border ${STRIPE_FIELD} h-auto`}>
      <CheckCircle2 className="size-4 shrink-0 text-green-600 dark:text-green-500" aria-hidden="true" />
      <span className="font-medium">Card saved</span>
      {pm?.last4 && (
        <span className="text-muted-foreground">
          {brand ? `${brand} ` : ''}•••• {pm.last4}
        </span>
      )}
    </div>
  );
}

// Mirror the app's shadcn tokens (app.css :root / .dark) into Stripe's iframe
// so the card fields read as our own inputs — same bg, border, focus ring, and
// type. Values are the rgb equivalents of the oklch tokens (Stripe's appearance
// validator predates oklch). The .AccordionItem reset kills the stock tinted
// panel behind the card fields.
export function buildAppearance(isDark: boolean): Appearance {
  const border = isDark ? 'rgba(255, 255, 255, 0.15)' : '#ebebeb';
  const ring = isDark ? 'rgba(142, 142, 142, 0.5)' : 'rgba(180, 180, 180, 0.5)';
  const ringBorder = isDark ? '#8e8e8e' : '#b4b4b4';
  const mutedForeground = isDark ? '#b5b5b5' : '#8e8e8e';
  return {
    theme: 'flat',
    variables: {
      // The cssSrc passed to <Elements> registers this exact family inside
      // Stripe's iframe from our own woff2 — same build as the app, so the
      // iframe text is indistinguishable from our labels.
      fontFamily: '"Geist Variable", system-ui, sans-serif',
      borderRadius: '8px',
      fontSizeBase: '14px',
      colorPrimary: '#0064E0',
      colorText: isDark ? '#fafafa' : '#252525',
      colorTextSecondary: mutedForeground,
      colorTextPlaceholder: mutedForeground,
      colorBackground: isDark ? '#262626' : '#ffffff',
      colorDanger: isDark ? '#e5484d' : '#dc3d43',
      iconColor: mutedForeground,
    },
    rules: {
      '.Input': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
        border: `1px solid ${border}`,
        boxShadow: 'none',
        padding: '6px 10px',
        // Pin the line box so the total height is exactly 34px (20 + 12
        // padding + 2 border) — the phone row below copies that number
        // (STRIPE_FIELD h-[34px]) to read as the same form.
        lineHeight: '20px',
      },
      '.Input:focus': {
        borderColor: ringBorder,
        boxShadow: `0 0 0 3px ${ring}`,
      },
      '.Label': {
        color: isDark ? '#fafafa' : '#252525',
        fontSize: '14px',
        fontWeight: '500',
      },
      // Unselected methods (the Google Pay / Apple Pay rows) render as
      // bordered input-like rows; the selected method's form stays flat so
      // the card fields sit directly in the dialog.
      '.AccordionItem': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
        border: `1px solid ${border}`,
        boxShadow: 'none',
        padding: '10px 12px',
      },
      '.AccordionItem--selected': {
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        padding: '0',
      },
      '.Block': {
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
      },
    },
  };
}
